import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Users, MapPin, Calendar, Clock, TrendingUp, Target, UserPlus } from "lucide-react";
import { POSITIONS, getPositionLabel } from "@/utils/positions";

const OpenPositionsPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [openPositions, setOpenPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [selectedDetailedPosition, setSelectedDetailedPosition] = useState("");
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    fetchOpenPositions();
  }, []);

  const fetchOpenPositions = async () => {
    try {
      const response = await axios.get(`${API}/open-positions`);
      setOpenPositions(response.data);
    } catch (error) {
      console.error("Error fetching open positions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!selectedDetailedPosition) {
      toast.error("Lütfen pozisyon seçin");
      return;
    }

    setApplying(true);
    try {
      const response = await axios.post(
        `${API}/open-positions/${selectedPosition.id}/apply`,
        {
          position: selectedDetailedPosition.split("-")[0],
          detailedPosition: selectedDetailedPosition
        }
      );
      toast.success(response.data.message);
      setApplyDialogOpen(false);
      fetchOpenPositions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Başvuru yapılamadı");
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 dark:bg-dark-bg dark:from-dark-bg dark:to-dark-bg transition-colors duration-300">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 dark:border-emerald-400"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 dark:bg-dark-bg dark:from-dark-bg dark:to-dark-bg transition-colors duration-300" data-testid="open-positions-page">
      <Navbar user={user} onLogout={onLogout} />
      
      <main className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-emerald-600 to-green-600 dark:from-emerald-400 dark:to-green-400 bg-clip-text text-transparent">
            Açık Pozisyonlar
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300">Eksik kişi olan maçlara katıl, yeni takımlar bul</p>
        </div>

        {/* Open Positions Grid */}
        {openPositions.length === 0 ? (
          <Card className="p-12 text-center">
            <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400 text-lg">Şu anda açık pozisyon bulunmuyor</p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {openPositions.map((position) => (
              <Card key={position.id} className="hover-lift cursor-pointer" data-testid={`position-card-${position.id}`}>
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <CardTitle className="text-xl">{position.venueName}</CardTitle>
                    <Badge className="bg-orange-100 text-orange-700">
                      {position.applicationsCount} Başvuru
                    </Badge>
                  </div>
                  <CardDescription className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    {position.venueLocation}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Match Info */}
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="h-4 w-4 text-emerald-600" />
                      <span>{position.date}</span>
                      <Clock className="h-4 w-4 text-emerald-600 ml-2" />
                      <span>{position.time}</span>
                    </div>

                    {/* Captain */}
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Users className="h-4 w-4 text-blue-600" />
                      <span>Kaptan: {position.captainName}</span>
                    </div>

                    {/* Team Average */}
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <TrendingUp className="h-4 w-4 text-purple-600" />
                      <span>Takım Ort: {position.teamAveragePoints} puan</span>
                    </div>

                    {/* Missing Positions */}
                    <div className="pt-3 border-t">
                      <p className="text-sm font-semibold mb-2 flex items-center gap-2">
                        <Target className="h-4 w-4 text-red-600" />
                        Aranan Pozisyonlar:
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {position.missingPositions.map((pos, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {getPositionLabel(pos)}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Participation Fee */}
                    {position.participationFee > 0 && (
                      <div className="p-2 bg-yellow-50 rounded text-center">
                        <span className="text-sm font-semibold text-yellow-700">
                          Katılım: {position.participationFee} TL
                        </span>
                      </div>
                    )}

                    {/* Description */}
                    {position.description && (
                      <p className="text-sm text-gray-600 italic">"{position.description}"</p>
                    )}

                    {/* Auto Accept Badge */}
                    {position.autoAccept && (
                      <Badge className="bg-green-100 text-green-700 text-xs">
                        🚀 Otomatik Kabul
                      </Badge>
                    )}

                    {/* Action Buttons */}
                    <div className="pt-3 space-y-2">
                      <Button
                        className="w-full bg-gradient-to-r from-emerald-500 to-green-600 dark:from-dark-card dark:to-dark-accent"
                        onClick={() => {
                          setSelectedPosition(position);
                          setApplyDialogOpen(true);
                        }}
                        data-testid={`apply-btn-${position.id}`}
                      >
                        <UserPlus className="h-4 w-4 mr-2" />
                        Takıma Başvur
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => navigate(`/matches/${position.matchId}`)}
                      >
                        Maç Detayı
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Apply Dialog */}
      <Dialog open={applyDialogOpen} onOpenChange={setApplyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pozisyon Seç</DialogTitle>
            <DialogDescription>
              {selectedPosition?.venueName} - {selectedPosition?.date} {selectedPosition?.time}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Oynamak istediğin pozisyon</Label>
              <Select value={selectedDetailedPosition} onValueChange={setSelectedDetailedPosition}>
                <SelectTrigger>
                  <SelectValue placeholder="Pozisyon seç" />
                </SelectTrigger>
                <SelectContent>
                  {selectedPosition?.missingPositions.map((pos) => (
                    <SelectItem key={pos} value={pos}>
                      {getPositionLabel(pos)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedPosition?.participationFee > 0 && (
              <div className="p-4 bg-yellow-50 rounded-lg">
                <p className="text-sm text-yellow-800">
                  💰 Katılım ücreti: <span className="font-bold">{selectedPosition.participationFee} TL</span>
                </p>
              </div>
            )}

            <Button
              onClick={handleApply}
              disabled={applying || !selectedDetailedPosition}
              className="w-full bg-gradient-to-r from-emerald-500 to-green-600 dark:from-dark-card dark:to-dark-accent"
            >
              {applying ? "Başvuruluyor..." : "Başvuruyu Gönder"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OpenPositionsPage;
